import { MONTHS } from '@vy/types';
import { isNotNullOrUndefined, sumBy } from '@vy/utils';
import {
  getRevenueKeysForType,
  ROW_LEVELS,
  type ExpandedCustomerRevenueReportRow,
} from './customer-revenue-report-row-expander';

export function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function splitDateHeader(dateHeader: string) {
  const separatorIndex = dateHeader.lastIndexOf(' ');
  const month = dateHeader.slice(0, separatorIndex);
  const year = dateHeader.slice(separatorIndex + 1);

  return { month, year };
}

const MONTH_NOT_FOUND = -1;
const JANUARY_INDEX = 0;

export function parseDateHeader(dateHeader: string): Date {
  const { month, year } = splitDateHeader(dateHeader);
  const monthIndex = MONTHS.indexOf(month as (typeof MONTHS)[number]);
  const resolvedMonthIndex = monthIndex === MONTH_NOT_FOUND ? JANUARY_INDEX : monthIndex;

  return new Date(Number(year), resolvedMonthIndex, 1);
}

export function getNumericRevenueForDate(
  row: ExpandedCustomerRevenueReportRow,
  dateHeader: string,
  allDateHeaders?: string[],
): number {
  if (row.revenueType === 'newDeferred') {
    return round(getBreakdownValue(row, dateHeader, 'scheduledCharge'));
  }

  if (row.revenueType === 'recognizedDeferred') {
    return round(getBreakdownValue(row, dateHeader, 'revenue'));
  }

  if (row.revenueType === 'endingDeferred') {
    return round(getBreakdownValue(row, dateHeader, 'deferredRevenue'));
  }

  if (row.revenueType === 'newUnbilledAR') {
    return round(getBreakdownValue(row, dateHeader, 'scheduledCharge'));
  }

  if (row.revenueType === 'recognizedUnbilled') {
    return round(getBreakdownValue(row, dateHeader, 'revenue'));
  }

  if (row.revenueType === 'endingUnbilled') {
    return round(getBreakdownValue(row, dateHeader, 'unbilledRevenue'));
  }

  const revenueKeys =
    row.level === ROW_LEVELS.Customer || row.level === ROW_LEVELS.Total || row.level === ROW_LEVELS.Product
      ? getRevenueKeysForType('revenue')
      : getRevenueKeysForType(row.revenueType);

  const { month, year } = splitDateHeader(dateHeader);

  const revenuePerMonth = row.yearlyRevenue[year];
  const breakdown = revenuePerMonth?.[month as keyof typeof revenuePerMonth];

  if (isNotNullOrUndefined(breakdown)) {
    let total = 0;
    for (const revenueKey of revenueKeys) {
      total += breakdown[revenueKey] ?? 0;
    }
    return round(total);
  }

  if (row.revenueType === 'unbilledRevenue' && allDateHeaders) {
    return round(getCarryForwardUnbilled(row, dateHeader, allDateHeaders));
  }

  return 0;
}

function getBreakdownValue(
  row: ExpandedCustomerRevenueReportRow,
  dateHeader: string,
  key: 'deferredRevenue' | 'revenue' | 'scheduledCharge' | 'unbilledRevenue',
): number {
  const { month, year } = splitDateHeader(dateHeader);
  const revenuePerMonth = row.yearlyRevenue[year];

  if (!isNotNullOrUndefined(revenuePerMonth)) {
    return 0;
  }

  const breakdown = revenuePerMonth[month as keyof typeof revenuePerMonth];

  if (!isNotNullOrUndefined(breakdown)) {
    return 0;
  }

  return breakdown[key] ?? 0;
}


function getCarryForwardUnbilled(
  row: ExpandedCustomerRevenueReportRow,
  dateHeader: string,
  allDateHeaders: string[],
): number {
  const targetTime = parseDateHeader(dateHeader).getTime();

  let lastUnbilled = 0;

  for (const header of allDateHeaders) {
    if (parseDateHeader(header).getTime() >= targetTime) {
      break;
    }

    const { month, year } = splitDateHeader(header);
    const breakdown = row.yearlyRevenue[year]?.[month as keyof (typeof row.yearlyRevenue)[string]];

    if (isNotNullOrUndefined(breakdown)) {
      lastUnbilled = breakdown.unbilledRevenue ?? 0;
    }
  }

  return lastUnbilled;
}

function getCurrentMonthStart() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function getRowRecognizedTotal(row: ExpandedCustomerRevenueReportRow, dateHeaders: string[]): number {
  const currentMonthStart = getCurrentMonthStart();
  const recognizedHeaders = dateHeaders.filter((header) => parseDateHeader(header) < currentMonthStart);

  return round(sumBy(recognizedHeaders, (header) => getRevenueForDate(row, header)));
}

export function getRowRemainingTotal(row: ExpandedCustomerRevenueReportRow, dateHeaders: string[]): number {
  const currentMonthStart = getCurrentMonthStart();
  const remainingHeaders = dateHeaders.filter((header) => parseDateHeader(header) >= currentMonthStart);

  return round(sumBy(remainingHeaders, (header) => getRevenueForDate(row, header)));
}

function getRevenueForDate(row: ExpandedCustomerRevenueReportRow, dateHeader: string): number {
  const { month, year } = splitDateHeader(dateHeader);
  const revenuePerMonth = row.yearlyRevenue[year];

  if (!isNotNullOrUndefined(revenuePerMonth)) {
    return 0;
  }

  const breakdown = revenuePerMonth[month as keyof typeof revenuePerMonth];

  if (!isNotNullOrUndefined(breakdown)) {
    return 0;
  }

  if (row.level === ROW_LEVELS.TotalChild) {
    const keys = getRevenueKeysForType(row.revenueType);
    let total = 0;
    for (const key of keys) {
      total += breakdown[key] ?? 0;
    }
    return total;
  }

  return breakdown.revenue ?? 0;
}
